import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 11,
    fontFamily: 'Helvetica',
  },
  title: {
    fontSize: 20,
    marginBottom: 16,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  metaTable: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#bfbfbf',
    marginBottom: 20,
  },
  metaRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#bfbfbf',
    borderBottomStyle: 'solid',
  },
  metaLabel: {
    width: '30%',
    padding: 8,
    fontSize: 10,
    color: '#666',
    borderRightWidth: 1,
    borderRightColor: '#bfbfbf',
    borderRightStyle: 'solid',
  },
  metaValue: {
    width: '70%',
    padding: 8,
    fontSize: 11,
    fontWeight: 'bold',
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#201e52',
  },
  agreementText: {
    fontSize: 10,
    lineHeight: 1.5,
  },
});

/** Strips HTML tags for a plain-text PDF render of the agreement content. */
function stripHtml(html: string): string {
  return html
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

interface SignedWaiverPdfProps {
  waiverName: string;
  tournamentName: string;
  teamName: string;
  signedBy: string;
  signedOn: string;
  agreementContent: string;
}

function SignedWaiverPdf({
  waiverName,
  tournamentName,
  teamName,
  signedBy,
  signedOn,
  agreementContent,
}: SignedWaiverPdfProps) {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>{waiverName}</Text>

        <View style={styles.metaTable}>
          <View style={styles.metaRow}>
            <Text style={styles.metaLabel}>Tournament</Text>
            <Text style={styles.metaValue}>{tournamentName}</Text>
          </View>
          <View style={styles.metaRow}>
            <Text style={styles.metaLabel}>Team</Text>
            <Text style={styles.metaValue}>{teamName}</Text>
          </View>
          <View style={styles.metaRow}>
            <Text style={styles.metaLabel}>Signed by</Text>
            <Text style={styles.metaValue}>{signedBy}</Text>
          </View>
          <View style={[styles.metaRow, { borderBottomWidth: 0 }]}>
            <Text style={styles.metaLabel}>Signed on</Text>
            <Text style={styles.metaValue}>{signedOn}</Text>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Waiver Agreement</Text>
        <Text style={styles.agreementText}>{stripHtml(agreementContent)}</Text>
      </Page>
    </Document>
  );
}

export async function exportSignedWaiverToPDF(props: SignedWaiverPdfProps): Promise<void> {
  const doc = <SignedWaiverPdf {...props} />;
  const asPdf = pdf(doc);
  const blob = await asPdf.toBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${props.waiverName.replace(/\s+/g, '_')}_Signed_${new Date().toISOString().split('T')[0]}.pdf`;
  link.click();
  URL.revokeObjectURL(url);
}
